// Consumer complaints: the register (migration 042; plan 2026-10-31, signed by Amit).
//
// Every case from ActiveCampaign pipeline 1 since 1 Jul 2026 (mirrored every 10 minutes), plus the
// ones recorded by hand. A row is red when its acknowledgement or close clock is over (SOP v14: ack 1
// day; close Low 30 days, Medium and High 14), amber while the 1-day ack clock runs. Severity shows
// who set it: a person here, or "in ActiveCampaign" (AC's shared login names nobody).
//
// Rule 15: a list that could not be read says so and offers to ask again; it is never "no complaints".
import { batchLine } from './ComplaintBatch';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/DataTable';
import { Saved } from '@/components/Saved';
import { api } from '../../lib/api';
import { UnmatchedMail } from './ComplaintConversation';
import { fieldClass, labelClass, officeFocus, serverMessage, useResource } from '../../shells/OfficeShell';
import {
  CHANNEL_LABEL,
  SEVERITY_TONE,
  byWhom,
  clockPhrase,
  day,
  newKey,
  when,
  type ComplaintView,
  type ComplaintsConfig,
} from './complaintsModel';

const col = createColumnHelper<ComplaintView>();

// The clock as a pill on its own background: coloured text on the row's tint failed AA in dark (axe, 1 Nov 2026).
const TONE_PILL = { bad: 'bg-(--out-bg) text-(--out-fg) font-semibold', warn: 'bg-(--action-bg) text-(--action-fg) font-semibold', ok: 'bg-(--in-bg) text-(--in-fg)', muted: 'text-(--muted)' } as const;

function SeverityChip({ c }: { c: ComplaintView }) {
  if (!c.severity) return <span className="rounded-full border border-dashed border-(--border-strong) px-2 py-0.5 text-[11px] text-(--muted)">not set</span>;
  return (
    <span className="flex flex-col gap-0.5">
      <span className={`w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold ${SEVERITY_TONE[c.severity.value]}`}>{c.severity.value}</span>
      {/* Who set it, only when a person did here: "in ActiveCampaign" on nearly every row said nothing (the footnote says it once). */}
      {c.severity.from === 'MIRROR' ? null : <span className="text-[11px] text-(--muted)">{byWhom(c.severity.from, c.severity.byUpn)}</span>}
    </span>
  );
}

/** The edge a row or card is marked on: red when a clock is over, amber while the 1-day ack clock runs. */
function accentOf(c: ComplaintView): 'bad' | 'warn' | null {
  if (c.closed) return null;
  const p = clockPhrase(c);
  return p.tone === 'bad' ? 'bad' : p.tone === 'warn' && !c.clocks.ackMet ? 'warn' : null;
}

/** What a phone search matches: what the table's search box would. */
const phoneText = (c: ComplaintView) =>
  [c.caseNo, c.product, c.batchCode, c.consumerName, c.category?.value, c.severity?.value, c.stageLabel].filter(Boolean).join(' ').toLowerCase();

/**
 * The register on a phone (UI check rule 7, 1 Nov 2026): one card per case, the clock beside the case number,
 * so the first screen shows which cases are late. The table, scrolled sideways, stays from sm up.
 */
function PhoneCards({ rows, loading, empty }: { rows: ComplaintView[]; loading: boolean; empty: string | null }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const shown = needle ? rows.filter((c) => phoneText(c).includes(needle)) : rows;
  if (loading && rows.length === 0) return <p className="m-0 text-(length:--fs-sm) text-(--muted)">Reading the complaints…</p>;
  return (
    <div className="flex flex-col gap-(--s2)">
      {rows.length > 10 ? (
        <div className="flex items-center gap-(--s2)">
          <label className="relative flex min-w-0 flex-1 items-center">
            <span className="sr-only">Search the complaints</span>
            <Search aria-hidden="true" className="pointer-events-none absolute left-[12px] h-[16px] w-[16px] text-(--muted)" />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Product, batch, consumer or case" className={`${fieldClass} ${officeFocus} w-full pl-[36px]`} />
          </label>
          <span className="shrink-0 text-[12px] text-(--muted)" aria-live="polite">
            {shown.length === rows.length ? `${rows.length} cases` : `${shown.length} of ${rows.length}`}
          </span>
        </div>
      ) : null}
      {shown.length === 0 ? (
        <p className="m-0 text-(length:--fs-sm) text-(--muted)">{rows.length ? 'Nothing matches the search.' : (empty ?? 'Nothing to show.')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-(--s2) p-0" aria-label="Consumer complaints, newest first">
          {shown.map((c) => {
            const p = clockPhrase(c);
            const accent = accentOf(c);
            const edge = accent === 'bad' ? 'shadow-[inset_3px_0_0_var(--bad-line)]' : accent === 'warn' ? 'shadow-[inset_3px_0_0_var(--warn-line)]' : '';
            return (
              <li key={c.id}>
                <Link to={`/office/complaints/${c.id}`} className={`flex flex-col gap-1 rounded-md border border-(--border) bg-(--card) px-(--s3) py-(--s2) text-(--fg) no-underline ${edge} ${officeFocus}`}>
                  <span className="flex items-center justify-between gap-(--s2)">
                    <span className="font-semibold underline">{c.caseNo}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[12px] tabular-nums ${TONE_PILL[p.tone]}`}>{p.text}</span>
                  </span>
                  <span className="text-(length:--fs-sm)">
                    {c.product ?? 'Product not given'}
                    <span className="text-(--muted) tabular-nums">{c.batchCode ? ` · batch ${c.batchCode}` : ''}</span>
                  </span>
                  <BatchTag b={c.batch} trace={c.trace} />
                  <span className="flex flex-wrap items-center gap-x-(--s2) gap-y-1 text-[12px] text-(--muted)">
                    {c.severity ? <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SEVERITY_TONE[c.severity.value]}`}>{c.severity.value}</span> : <span>severity not set</span>}
                    {c.category ? <span>{c.category.value}</span> : null}
                    <span>{when(c.receivedAt)}</span>
                    {c.sameBatch.length ? <span>same batch: {c.sameBatch.length} other{c.sameBatch.length === 1 ? '' : 's'}</span> : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const EMPTY_HAND = { channel: '', consumerName: '', product: '', batchCode: '', bestBefore: '', placeOfPurchase: '', message: '' };

function RecordByHand({ config, onDone, onClose }: { config: ComplaintsConfig | null; onDone: (said: string, id: number) => void; onClose: () => void }) {
  const [key, setKey] = useState(newKey);
  const [form, setForm] = useState(EMPTY_HAND);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ id: number }>('/complaints', { clientKey: key, ...form });
      setKey(newKey());
      setForm(EMPTY_HAND);
      onDone(`Recorded the complaint (${CHANNEL_LABEL[form.channel] ?? form.channel}) as CMP-${String(r.id).padStart(4, '0')}.`, r.id);
    } catch (err) {
      setError(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }
  const channels = (config?.channels ?? []).filter((c) => c !== 'FORM');
  return (
    <form
      className="flex w-full max-w-[720px] flex-col gap-(--s2) rounded-md border border-(--border) bg-(--card) p-(--s4)"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <p className="m-0 font-semibold">A complaint that did not come through the form</p>
      <div className="grid grid-cols-1 gap-(--s2) sm:grid-cols-3">
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>How it came in (required)</span>
          <select id="hand-channel" autoFocus className={`${fieldClass} ${officeFocus}`} value={form.channel} onChange={set('channel')} required>
            <option value="">Choose</option>
            {channels.map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c] ?? c}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Consumer</span>
          <input id="hand-name" className={`${fieldClass} ${officeFocus}`} value={form.consumerName} onChange={set('consumerName')} />
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Bought at</span>
          <input id="hand-place" className={`${fieldClass} ${officeFocus}`} value={form.placeOfPurchase} onChange={set('placeOfPurchase')} />
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Product</span>
          <input id="hand-product" className={`${fieldClass} ${officeFocus}`} value={form.product} onChange={set('product')} />
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Batch code</span>
          <input id="hand-batch" className={`${fieldClass} ${officeFocus}`} value={form.batchCode} onChange={set('batchCode')} />
        </label>
        <label className="flex flex-col gap-(--s1)">
          <span className={labelClass}>Best before</span>
          <input id="hand-bb" className={`${fieldClass} ${officeFocus}`} value={form.bestBefore} onChange={set('bestBefore')} />
        </label>
      </div>
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>What the consumer said (required)</span>
        <textarea id="hand-message" rows={4} className={`${fieldClass} ${officeFocus}`} value={form.message} onChange={set('message')} required />
      </label>
      {error ? (
        <p role="alert" className="m-0 text-(length:--fs-sm) font-semibold text-(--warn)">
          {error}
        </p>
      ) : null}
      <div className="flex gap-(--s2)">
        <Button type="submit" disabled={busy}>
          {busy ? 'Recording…' : 'Record complaint'}
        </Button>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/** The resolved D365 batch under the printed code (rule 36): the register's one line of it. */
function BatchTag({ b, trace }: { b: ComplaintView['batch']; trace?: ComplaintView['trace'] }) {
  const line = batchLine(b);
  if (!line) return null;
  const tone = line.tone === 'ok' ? 'text-(--in-fg)' : line.tone === 'warn' ? 'text-(--warn)' : 'text-(--muted)';
  return (
    <span className="flex flex-wrap items-center gap-x-(--s2)">
      <span className={`text-[11px] font-semibold tabular-nums ${tone}`}>{line.text}</span>
      {/* The trace's own finding count (051): what the case page will show first. */}
      {trace?.flagged ? <span className="rounded-full bg-(--action-bg) px-2 py-0.5 text-[11px] font-semibold text-(--action-fg)">trace: {trace.flagged} flagged</span> : null}
    </span>
  );
}

export function Complaints() {
  const [status, setStatus] = useState<'open' | 'closed' | 'all'>('open');
  const config = useResource<ComplaintsConfig>('/complaints/config');
  const list = useResource<{ complaints: ComplaintView[]; acConfigured: boolean }>(`/complaints?status=${status}`);
  const [saved, setSaved] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [handOpen, setHandOpen] = useState(false);
  const handTrigger = useRef<HTMLButtonElement | null>(null);
  const closeHand = () => {
    setHandOpen(false);
    requestAnimationFrame(() => handTrigger.current?.focus());
  };
  const rows = list.data?.complaints ?? [];
  const open = rows.filter((c) => !c.closed);
  const notAcked = open.filter((c) => !c.clocks.ackMet).length;
  const over = open.filter((c) => c.clocks.ackMet && c.clocks.closeOverDays > 0).length;
  const highOpen = open.filter((c) => c.severity?.value === 'High').length;
  const noSeverity = open.filter((c) => !c.severity).length;

  async function sync() {
    setSyncing(true);
    setSaved(null);
    setSyncError(null);
    try {
      const r = await api.post<{ summary: { deals: number; created: number; activities: number; writtenBack: number; failedDeals: number } }>('/complaints/sync');
      const s = r.summary;
      if (s.failedDeals < 0) setSyncError('Could not read ActiveCampaign. Nothing was changed; try again in a minute.');
      else setSaved(`Read ${s.deals} deals from ActiveCampaign: ${s.created} new, ${s.activities} new emails or stage moves, ${s.writtenBack} written back${s.failedDeals ? `, ${s.failedDeals} could not be read` : ''}.`);
      list.reload();
    } catch (err) {
      setSyncError(serverMessage(err));
    } finally {
      setSyncing(false);
    }
  }

  const columns = [
    col.accessor((c) => c.caseNo, {
      id: 'case',
      header: 'Case',
      cell: (x) => (
        <span className="flex flex-col">
          <Link to={`/office/complaints/${x.row.original.id}`} className={`font-semibold underline ${officeFocus}`}>
            {x.getValue()}
          </Link>
          <span className="text-[11px] text-(--muted)">
            {x.row.original.acDealId ? `AC ${x.row.original.acDealId}` : CHANNEL_LABEL[x.row.original.channel]}
            {x.row.original.consumerName ? ` · ${x.row.original.consumerName}` : ''}
          </span>
        </span>
      ),
      meta: { rowHeader: true },
    }),
    col.accessor((c) => c.receivedAt, { id: 'received', header: 'Received', cell: (x) => when(x.getValue()), meta: { nowrap: true } }),
    col.accessor((c) => `${c.product ?? ''} ${c.batchCode ?? ''}`, {
      id: 'product',
      header: 'Product · batch',
      cell: (x) => (
        <span className="flex flex-col">
          <span>{x.row.original.product ?? 'not given'}</span>
          <span className="text-[11px] text-(--muted) tabular-nums">
            {x.row.original.batchCode ? `batch ${x.row.original.batchCode}` : 'no batch'}
            {x.row.original.bestBefore ? ` · BB ${x.row.original.bestBefore}` : ''}
          </span>
          <BatchTag b={x.row.original.batch} trace={x.row.original.trace} />
        </span>
      ),
    }),
    col.accessor((c) => c.category?.value ?? '', { id: 'category', header: 'Category', cell: (x) => x.getValue() || <span className="text-(--muted)">not set</span> }),
    col.accessor((c) => c.severity?.value ?? '', { id: 'severity', header: 'Severity', cell: (x) => <SeverityChip c={x.row.original} /> }),
    col.accessor((c) => c.stageLabel, { id: 'stage', header: 'Stage' }),
    col.accessor((c) => c.owner?.upn ?? '', {
      id: 'owner',
      header: 'Owner',
      cell: (x) => (x.getValue() ? byWhom('APP', x.getValue()) : <span className="text-(--muted)">nobody yet</span>),
    }),
    col.accessor((c) => c.clocks.closeOverDays, {
      id: 'clock',
      header: 'Clock',
      cell: (x) => {
        const p = clockPhrase(x.row.original);
        return <span className={`rounded-full px-2 py-0.5 text-[12px] tabular-nums ${TONE_PILL[p.tone]}`}>{p.text}</span>;
      },
      meta: { nowrap: true },
    }),
    col.accessor((c) => c.sameBatch.length, {
      id: 'flags',
      header: 'Flags',
      cell: (x) => {
        const c = x.row.original;
        const bits: string[] = [];
        if (c.sameBatch.length) bits.push(`same batch: ${c.sameBatch.length} other${c.sameBatch.length === 1 ? '' : 's'}`);
        if (c.notInAc.length) bits.push('not yet in ActiveCampaign');
        return bits.length ? <span className="text-[12px] text-(--muted)">{bits.join(' · ')}</span> : null;
      },
    }),
  ];

  return (
    <div className="flex flex-col gap-(--s5)">
      <header className="flex flex-wrap items-end justify-between gap-(--s3)">
        <div>
          <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Complaints</h1>
          <p className="m-0 mt-(--s1) max-w-[80ch] text-(length:--fs-sm) text-(--muted)">
            Acknowledge in 1 day; close Low in 30 days, Medium and High in 14 (SOP v14).
          </p>
        </div>
        <div className="flex flex-wrap gap-(--s2)">
          {/* Everyone's (QA team feedback, 2 Nov 2026), so offered even when the register refuses. */}
          <Button asChild variant="outline">
            <Link to="/office/complaints/analysis">Analysis</Link>
          </Button>
          <Button variant="outline" type="button" onClick={() => void sync()} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </Button>
          <Button ref={handTrigger} variant="outline" type="button" aria-expanded={handOpen} aria-controls="record-by-hand" onClick={() => (handOpen ? closeHand() : setHandOpen(true))}>
            Record by hand
          </Button>
        </div>
      </header>

      {handOpen ? (
        <div id="record-by-hand">
          <RecordByHand
            config={config.data}
            onClose={closeHand}
            onDone={(said) => {
              setSaved(said);
              closeHand();
              list.reload();
            }}
          />
        </div>
      ) : null}
      {syncError ? (
        <p role="alert" className="m-0 font-semibold text-(--warn)">
          {syncError}
        </p>
      ) : null}

      {config.data && !config.data.enabled ? (
        <p role="status" className="m-0 font-semibold text-(--warn)">
          Complaints are not switched on yet.
        </p>
      ) : null}
      {list.data && !list.data.acConfigured ? (
        <p role="status" className="m-0 font-semibold text-(--warn)">
          ActiveCampaign cannot be reached from the app (no token set). Cases shown may be out of date.
        </p>
      ) : null}


      {list.data ? (
        <dl className="m-0 grid grid-cols-2 gap-(--s3) sm:grid-cols-4">
          {[
            ['Open', open.length, `of ${rows.length} shown`, ''],
            ['Not acknowledged', notAcked, '1-day clock', notAcked ? 'text-(--action-fg)' : ''],
            ['Over the close clock', over, '14 or 30 days', over ? 'text-(--out-fg)' : ''],
            ['High open', highOpen, `${noSeverity} with no severity yet`, ''],
          ].map(([label, n, sub, tone]) => (
            <div key={label as string} className="rounded-md border border-(--border) bg-(--card) px-(--s3) py-(--s2) sm:px-(--s4) sm:py-(--s3)">
              <dt className="text-[11px] font-semibold uppercase tracking-[0.06em] text-(--muted)">{label}</dt>
              <dd className={`m-0 font-display text-[24px] font-semibold leading-tight tabular-nums sm:text-[30px] ${tone}`}>{n}</dd>
              {/* The phone keeps the numbers; the explanations wait for a wider screen. */}
              <dd className="m-0 hidden text-[12px] text-(--muted) sm:block">{sub}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      <label className="flex w-fit flex-col gap-(--s1)">
        <span className={labelClass}>Show</span>
        <select id="complaints-status" className={`${fieldClass} ${officeFocus}`} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
          <option value="all">All</option>
        </select>
      </label>
      {saved ? <Saved>{saved}</Saved> : null}
      {list.error ? (
        <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">Could not read the complaints: {list.error} This is not the same as "none".</p>
          <Button variant="outline" type="button" onClick={list.reload}>
            Try again
          </Button>
        </div>
      ) : (
        <>
          <div className="sm:hidden">
            <PhoneCards rows={rows} loading={list.loading} empty={list.data ? (status === 'open' ? 'No open complaints.' : 'No complaints.') : null} />
          </div>
          <div className="hidden sm:block">
        <DataTable
          data={rows}
          columns={columns}
          caption="Consumer complaints, newest first"
          getRowId={(c) => String(c.id)}
          rowHref={(c) => `/office/complaints/${c.id}`}
          rowAccent={accentOf}
          loading={list.loading}
          search={rows.length > 10 ? 'Search product, batch, consumer or case' : undefined}
          stickyFirst
          empty={list.data ? (status === 'open' ? 'No open complaints.' : 'No complaints.') : null}
        />
          </div>
        </>
      )}
      {config.data?.pilot ? (
        <UnmatchedMail
          onAttached={(said) => {
            setSaved(said);
            list.reload();
          }}
        />
      ) : null}
      <p className="m-0 text-[12px] text-(--muted)">
        Times are Brisbane time. A severity with no name under it was set in ActiveCampaign.{rows.length ? ` Oldest case shown: ${day(rows[rows.length - 1].receivedAt)}.` : ''}
      </p>
    </div>
  );
}
